// src/features/FieldService/WorkOrderAnalytics.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  LinearProgress,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  TrendingUp,
  Schedule,
  CheckCircle,
  Warning,
  Assignment
} from '@mui/icons-material';
import { useI18n } from '../../i18n/i18n';
import { WorkOrderService } from '../../services/workOrderService';

const WorkOrderAnalytics = ({ user }) => {
  const { t } = useI18n();
  const [workOrderService] = useState(new WorkOrderService());
  const [analytics, setAnalytics] = useState({
    total: 0,
    completed: 0,
    inProgress: 0,
    overdue: 0,
    avgCompletionTime: 0
  });
  const [timeRange, setTimeRange] = useState('30days');

  useEffect(() => {
    loadAnalytics();
  }, [timeRange]);

  const loadAnalytics = () => {
    const allOrders = workOrderService.workOrders;
    const filteredOrders = filterOrdersByTimeRange(allOrders, timeRange);
    
    const completed = filteredOrders.filter(wo => wo.status === 'completed');
    const inProgress = filteredOrders.filter(wo => wo.status === 'in_progress');
    const overdue = filteredOrders.filter(wo => 
      wo.status !== 'completed' && new Date(wo.dueDate) < new Date()
    );

    const avgCompletionTime = completed.length > 0 ? 
      completed.reduce((sum, wo) => sum + (wo.actualTime || 0), 0) / completed.length : 0;

    setAnalytics({
      total: filteredOrders.length,
      completed: completed.length,
      inProgress: inProgress.length,
      overdue: overdue.length,
      avgCompletionTime: Math.round(avgCompletionTime)
    });
  };

  const filterOrdersByTimeRange = (orders, range) => {
    const now = new Date();
    let startDate;

    switch (range) {
      case '7days':
        startDate = new Date(now.setDate(now.getDate() - 7));
        break;
      case '30days':
        startDate = new Date(now.setDate(now.getDate() - 30));
        break;
      case '90days':
        startDate = new Date(now.setDate(now.getDate() - 90));
        break;
      default:
        return orders;
    }

    return orders.filter(order => new Date(order.createdAt) >= startDate);
  };

  const getEfficiencyRate = () => {
    return analytics.total > 0 ? 
      Math.round((analytics.completed / analytics.total) * 100) : 0;
  };

  const getTopPerformers = () => {
    const technicians = {};
    
    workOrderService.workOrders.forEach(order => {
      if (order.status === 'completed') {
        if (!technicians[order.assignedTo]) {
          technicians[order.assignedTo] = {
            name: order.assignedTo,
            completed: 0,
            totalTime: 0,
            avgTime: 0
          };
        }
        technicians[order.assignedTo].completed++;
        technicians[order.assignedTo].totalTime += order.actualTime || 0;
      }
    });

    return Object.values(technicians)
      .map(tech => ({
        ...tech,
        avgTime: tech.completed > 0 ? Math.round(tech.totalTime / tech.completed) : 0
      }))
      .sort((a, b) => b.completed - a.completed)
      .slice(0, 5);
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        {t('work_order_analytics_title')}
      </Typography>
      
      {/* Фільтр за періодом */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {t('performance_overview')}
            </Typography>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>{t('time_period')}</InputLabel>
              <Select
                value={timeRange}
                label={t('time_period')}
                onChange={(e) => setTimeRange(e.target.value)}
              >
                <MenuItem value="7days">{t('last_7_days')}</MenuItem>
                <MenuItem value="30days">{t('last_30_days')}</MenuItem>
                <MenuItem value="90days">{t('last_90_days')}</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </CardContent>
      </Card>

      {/* Статистика */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <Assignment sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
            <Typography variant="h4" color="primary.main" fontWeight="bold">
              {analytics.total}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('total_orders')}
            </Typography>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <CheckCircle sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
            <Typography variant="h4" color="success.main" fontWeight="bold">
              {analytics.completed}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('completed')}
            </Typography>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <Schedule sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
            <Typography variant="h4" color="warning.main" fontWeight="bold">
              {analytics.inProgress}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('in_progress')}
            </Typography>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <Warning sx={{ fontSize: 40, color: 'error.main', mb: 1 }} />
            <Typography variant="h4" color="error.main" fontWeight="bold">
              {analytics.overdue}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('overdue')}
            </Typography>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <TrendingUp sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
            <Typography variant="h4" color="info.main" fontWeight="bold">
              {analytics.avgCompletionTime}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('avg_completion_min')}
            </Typography>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Ефективність */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('efficiency_rate')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={getEfficiencyRate()}
                  sx={{ flexGrow: 1, height: 20, borderRadius: 10 }}
                  color={getEfficiencyRate() > 80 ? 'success' : getEfficiencyRate() > 60 ? 'warning' : 'error'}
                />
                <Typography variant="h6" sx={{ ml: 2, minWidth: 50 }}>
                  {getEfficiencyRate()}%
                </Typography>
              </Box>
              <Stack spacing={1}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">{t('completed_orders')}</Typography>
                  <Typography variant="body2" fontWeight="bold">{analytics.completed}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">{t('total_orders')}</Typography>
                  <Typography variant="body2" fontWeight="bold">{analytics.total}</Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Топ техніки */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('top_performers')}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>{t('technician')}</strong></TableCell>
                      <TableCell><strong>{t('completed')}</strong></TableCell>
                      <TableCell><strong>{t('avg_time')}</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getTopPerformers().map((tech, index) => (
                      <TableRow key={tech.name}>
                        <TableCell>
                          <Typography variant="body2">
                            {tech.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={tech.completed} 
                            color={index === 0 ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {tech.avgTime} {t('minutes')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default WorkOrderAnalytics;